import React, { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import ClayLayout from '@clayui/layout';
import Button from '@clayui/button';
import {
    BusinessDetailsFields,
    columns,
    previousExperienceField
} from '../../utils/Config';
import FormComponent from '../../generic-components/formComponent/FormComponent';
import Loader from '../../generic-components/loader/Loader';
import { useAppDispatch, useAppSelector } from '../../store/hooks';
import { updateFormValues } from '../../slices/businessExpansionSlice';
import { updateCommonValues } from '../../slices/commonSlice';
import { useFetchListTypeEntries } from '../../services/ListTypeEntryService';
import IndustriesSelect from '../IndustriesSelect/IndustriesSelect';
import DynamicTable from '../../generic-components/DynamicTable/DynamicTable';
import { primaryButtonStyle, scrollToSection } from '../../utils/commonUtil';
import { expansionInquiryService } from '../../services/expansionInquiryService';
import { toast } from 'react-toastify';
import { useNavigate } from 'react-router-dom';
import { convertFilesToBase64 } from '../../utils/FileToBase64';
import { ListTypeEntry } from '../../context/ListTypeEntriesProvider';
import { serializePayload } from '../../utils/commonUtils';
interface IBusinessDetail {
    setStep: React.Dispatch<React.SetStateAction<number>>;
    setResStatusCode: React.Dispatch<React.SetStateAction<number>>;
    setResStatusMessage: React.Dispatch<React.SetStateAction<string>>;
}

const BusinessDetail: React.FC<IBusinessDetail> = ({
    setStep,
    setResStatusCode,
    setResStatusMessage
}) => {
    const dispatch = useAppDispatch();
    const formValues = useAppSelector((state) => state.businessExpansion);
    const commonValues = useAppSelector((state) => state.common);
    const annualRevenue = useFetchListTypeEntries('ANNUAL_REVENUE');
    const businessPurpose = useFetchListTypeEntries(
        'EXPANSION_BUSINESS_PURPOSE'
    );
    const employees = useFetchListTypeEntries('NUMBER_OF_EMPLOYEES');
    const targetMarket = useFetchListTypeEntries('TARGET_MARKETS', 'name:asc');
    const companyHeadQuarter = useFetchListTypeEntries(
        'LEAD_COMPANY_HEADQUARTER'
    );
    const productsManufactured = useFetchListTypeEntries(
        'LEAD_ARE_YOUR_PRODUCTS_MANUFACTURED_IN_DUBAI'
    );
    const country = useFetchListTypeEntries('COUNTRY');
    const yearOfOperating = useFetchListTypeEntries(
        'IMPORT_REQUEST_FORM_YEARS_OPERATING'
    );
    const marketPriority = useFetchListTypeEntries(
        'LEAD_MANAGEMENT_MARKET_PRIORITY'
    );
    const [isLoading, setIsLoading] = useState(false);
    const [previousCountryData, setPreviousCountryData] = useState(
        formValues.previousFromCountryData || []
    );
    const [businessExpandData, setBusinessExpandData] = useState(
        formValues.businessExpandCountryData || [
            {
                targetMarket: '',
                purposeOfExpansion: [],
                previousExperience: '',
                marketPriority: ''
            }
        ]
    );
    const [tableErrors, setTableErrors] = useState<string[]>([]);
    const [industryOptions, setIndustryOptions] = useState<any[]>([]);
    const [subIndustryOptions, setSubIndustryOptions] = useState<any[]>([]);

    const {
        control,
        register,
        formState: { errors: formErrors },
        watch,
        handleSubmit,
        setError,
        getValues,
        reset,
        setValue
    } = useForm<any>({
        mode: 'all',
        defaultValues: { ...formValues, ...commonValues }
    });
    const industryValue = watch('industry');

    const managedFields = React.useMemo(
        () => [
            ...BusinessDetailsFields.map((f) => f.name),
            ...previousExperienceField.map((f) => f.name),
            'industrySubcategory'
        ],
        []
    );

    const getManagedValues = (currentValues: any) => {
        const payload: any = {};
        managedFields.forEach((name) => {
            if (currentValues[name] !== undefined) {
                payload[name] = currentValues[name];
            }
        });
        // payload.previousFromCountryData = previousCountryData;
        payload.businessExpandCountryData = businessExpandData;
        return payload;
    };

    useEffect(() => {
        reset({ ...formValues, ...commonValues });
    }, [reset]);

    useEffect(() => {
        return () => {
            const values = getManagedValues(getValues());
            dispatch(updateFormValues(values));
            dispatch(updateCommonValues(values));
        };
    }, [
        dispatch,
        getValues,
        managedFields,
        previousCountryData,
        businessExpandData
    ]);
    const navigate = useNavigate();
    const [dropdownOptions, setDropdownOptions] = useState<any>({});

    const validateTable = (data: any[]) => {
        const errors: string[] = [];
        let hasPrimary = false;
        data.forEach((row, index) => {
            const hasPurposeOfExpansion =
                Array.isArray(row.purposeOfExpansion) &&
                row.purposeOfExpansion.length > 0;
            if (
                !row.targetMarket ||
                !hasPurposeOfExpansion ||
                !row.previousExperience ||
                !row.marketPriority
            ) {
                errors.push(`Row ${index + 1}: fill the data`);
            }

            const priorityOpt = marketPriority?.find(
                (opt: any) => String(opt.key) === String(row.marketPriority)
            );

            if (
                (priorityOpt &&
                    (priorityOpt.name.toLowerCase().includes('primary') ||
                        String(priorityOpt.key)
                            .toLowerCase()
                            .includes('primary'))) ||
                (row.marketPriority &&
                    String(row.marketPriority)
                        .toLowerCase()
                        .includes('primary'))
            ) {
                hasPrimary = true;
            }
        });

        if (data.length > 0 && !hasPrimary) {
            errors.push(
                'At least one row must have Primary Countries as Market Priority'
            );
        }

        return errors;
    };

    const isAddDisabled = React.useMemo(() => {
        if (businessExpandData.length === 0) return false;
        const lastRow = businessExpandData[businessExpandData.length - 1];
        const hasPurposeOfExpansion =
            Array.isArray(lastRow.purposeOfExpansion) &&
            lastRow.purposeOfExpansion.length > 0;
        return (
            !lastRow.targetMarket ||
            !hasPurposeOfExpansion ||
            !lastRow.previousExperience ||
            !lastRow.marketPriority
        );
    }, [businessExpandData]);

    const onSubmit = async (data: any) => {
        const errors = validateTable(businessExpandData);
        const {
            isFormFilled,
            isChamberMember,
            industry,
            industrySubcategory,
            haveYouFill,
            attachtradeLicenseDoc,
            attachMembershipDoc,
            attachCompanyProfile,
            previousFromCountryData,
            businessExpandCountryData,
            formId,
            currentInternationalMarketsCompany,
            ...rest
        } = data;
        const tradeLicenseDoc = await convertFilesToBase64(
            attachtradeLicenseDoc
        );
        const companyProfileDoc =
            await convertFilesToBase64(attachCompanyProfile);
        const membershipDoc = await convertFilesToBase64(attachMembershipDoc);

        console.log('res', rest);
        if (errors.length > 0) {
            setTableErrors(errors);
            return;
        }

        const industryNames = (Array.isArray(industry) ? industry : [industry])
            .map(
                (id) =>
                    industryOptions.find(
                        (opt) => opt.key.toString() === id.toString()
                    )?.name
            )
            .filter(Boolean)
            .join(', ');

        const subIndustryNames = (
            Array.isArray(industrySubcategory)
                ? industrySubcategory
                : [industrySubcategory]
        )
            .map(
                (id) =>
                    subIndustryOptions.find(
                        (opt) => opt.key.toString() === id.toString()
                    )?.name
            )
            .filter(Boolean)
            .join(', ');

        const internationalMarketNames = (
            Array.isArray(currentInternationalMarketsCompany)
                ? currentInternationalMarketsCompany
                : [currentInternationalMarketsCompany]
        )
            .filter(Boolean)
            .join(', ');

        const payload = {
            ...rest,
            ...(data?.formId > 0 && { id: data?.formId }),
            currentInternationalMarkets: internationalMarketNames,
            businessSectorsYourCompanyIsEngagedIn: industryNames,
            areYouADubaiChamberMember: isChamberMember ?? true,
            businessSubSector: subIndustryNames,
            scopeKey: 'Lead Management',
            leadVersion: formValues?.inquiryVersion,
            dcMembershipCertificate: membershipDoc?.length
                ? {
                      fileBase64: membershipDoc[0].fileBase64,
                      name: membershipDoc[0].name
                  }
                : null,
            attachCompanyProfile: companyProfileDoc?.length
                ? {
                      fileBase64: companyProfileDoc[0].fileBase64,
                      name: companyProfileDoc[0].name
                  }
                : null,
            attachTradeLicense: tradeLicenseDoc?.length
                ? {
                      fileBase64: tradeLicenseDoc[0].fileBase64,
                      name: tradeLicenseDoc[0].name
                  }
                : null,
            targetMarketsList: businessExpandData
                ?.filter((item) => item?.marketPriority == 'primaryCountries')
                .map((item) => {
                    const market = targetMarket?.find(
                        (opt: any) =>
                            String(opt.key) === String(item?.targetMarket)
                    );
                    console.log('market', market);
                    let otherPurpose = '';
                    const purposeKeys = (
                        Array.isArray(item?.purposeOfExpansion)
                            ? item.purposeOfExpansion
                            : []
                    )
                        .map((val: string | number) => {
                            const queryKey = String(val).includes(': ')
                                ? String(val).split(': ')[0]
                                : String(val);
                            if (String(val).includes(': ')) {
                                const subText = String(val).split(': ')[1];
                                otherPurpose = subText;
                            }
                            return queryKey;
                        })
                        .filter(Boolean);

                    return {
                        ...item,
                        targetMarketKey: item?.targetMarket,
                        targetMarket: market?.name ?? item?.targetMarket,
                        businessPurpose:
                            purposeKeys || item?.purposeOfExpansion,
                        otherBusinessPurpose: otherPurpose,
                        previousExperience:
                            item?.previousExperience?.toLowerCase() === 'yes'
                                ? true
                                : false
                    };
                }),
            secondaryTargetMarketsList:
                businessExpandData
                    ?.filter(
                        (item) => item?.marketPriority == 'secondaryCountries'
                    )
                    .map((item) => {
                        const market = targetMarket?.find(
                            (opt: any) =>
                                String(opt.key) === String(item?.targetMarket)
                        );
                        console.log('market', market);
                        let otherPurpose = '';
                        const purposeKeys = (
                            Array.isArray(item?.purposeOfExpansion)
                                ? item.purposeOfExpansion
                                : []
                        )
                            .map((val: string | number) => {
                                const queryKey = String(val).includes(': ')
                                    ? String(val).split(': ')[0]
                                    : String(val);
                                if (String(val).includes(': ')) {
                                    const subText = String(val).split(': ')[1];
                                    otherPurpose = subText;
                                }
                                return queryKey;
                            })
                            .filter(Boolean);

                        return {
                            ...item,
                            targetMarketKey: item?.targetMarket,
                            targetMarket: market?.name ?? item?.targetMarket,
                            businessPurpose:
                                purposeKeys || item?.purposeOfExpansion,
                            otherBusinessPurpose: otherPurpose,
                            previousExperience:
                                item?.previousExperience?.toLowerCase() ===
                            'yes'
                                    ? true
                                    : false
                        };
                    }) || []
        };
        setTableErrors([]);
        setIsLoading(true);
        try {
            const sanitizedPayload = serializePayload(payload);
            const response = (await expansionInquiryService.createInquiry(
                sanitizedPayload
            )) as {
                success?: boolean;
                message?: string;
                data?: {
                    statusCode?: number;
                    statusMessage?: string;
                };
            };

            if (response.success && response.data?.statusCode === 200) {
                toast.success('Expansion Inquiry Added Successfully', {
                    theme: 'light',
                    autoClose: 2000
                });
                setResStatusCode(Number(response?.data?.statusCode));
                setResStatusMessage('Expansion Inquiry Added Successfully');
            } else if (response.data?.statusCode) {
                toast.error(
                    response.data.statusMessage || 'Failed to submit inquiry',
                    {
                        theme: 'light',
                        autoClose: 4000
                    }
                );
            } else {
                toast.error(response.message || 'Failed to submit inquiry', {
                    theme: 'light',
                    autoClose: 4000
                });
            }
        } catch (err: any) {
            toast.error(err?.message || 'Failed to submit inquiry', {
                theme: 'light',
                autoClose: 4000
            });
            setResStatusCode(Number(err?.data?.statusCode));
            setResStatusMessage(
                err?.data?.statusMessage ?? 'Failed to submit inquiry'
            );
        } finally {
            setIsLoading(false);
            navigate('/');
            setStep(4);
            // setTimeout(() => {
            //     window.scrollTo(0, 0);
            // }, 1000);
            scrollToSection();
        }
        const values = getManagedValues(data);
        dispatch(updateFormValues(values));
        dispatch(updateCommonValues(values));
    };

    useEffect(() => {
        if (tableErrors.length > 0) {
            const errors = validateTable(businessExpandData);
            if (errors.length < tableErrors.length) {
                setTableErrors(errors);
            }
        }
    }, [businessExpandData]);

    useEffect(() => {
        const updateDropdownOptions = async () => {
            const numberOfEmployees = employees;
            const isYourCompanyHeadquarteredInDubai = companyHeadQuarter;
            const numberOfYearsOperatingInDubai = yearOfOperating;
            const areYourProductsManufacturedInDubai = productsManufactured;
            const currentInternationalMarketsCompany = country;
            setDropdownOptions((prevData: any) => ({
                ...prevData,
                numberOfEmployees,
                isYourCompanyHeadquarteredInDubai,
                numberOfYearsOperatingInDubai,
                annualRevenue,
                businessPurpose,
                areYourProductsManufacturedInDubai,
                currentInternationalMarketsCompany
            }));
        };

        updateDropdownOptions();
    }, [
        annualRevenue,
        employees,
        companyHeadQuarter,
        yearOfOperating,
        businessPurpose,
        productsManufactured,
        country
    ]);

    const businessPurposeValue = watch('businessPurpose');

    const currentInternationalMarketsCompanyValue = watch(
        'currentInternationalMarketsCompany'
    );
    const prevInternationalMarkets = React.useRef<any[]>([]);

    useEffect(() => {
        const current = currentInternationalMarketsCompanyValue || [];
        const previous = prevInternationalMarkets.current || [];

        if (Array.isArray(current) && current.length > 1) {
            const allOption = country?.find(
                (item: any) => item?.name?.trim()?.toLowerCase() === 'all'
            );

            if (allOption) {
                const allKey = String(allOption.key);
                const hasAllNow = current.some((val) => String(val) === allKey);
                const hadAllBefore = previous.some(
                    (val) => String(val) === allKey
                );

                if (hasAllNow && !hadAllBefore) {
                    setValue('currentInternationalMarketsCompany', [allKey]);
                } else if (hasAllNow && hadAllBefore) {
                    const newValues = current.filter(
                        (val: any) => String(val) !== allKey
                    );
                    setValue('currentInternationalMarketsCompany', newValues);
                }
            }
        }
        prevInternationalMarkets.current = current;
    }, [currentInternationalMarketsCompanyValue, country, setValue]);

    const showSpecifyOther = React.useMemo(() => {
        if (!businessPurposeValue) return false;
        if (Array.isArray(businessPurposeValue)) {
            return businessPurposeValue.some(
                (v: any) =>
                    (typeof v === 'string' &&
                        v.toLowerCase().includes('other')) ||
                    (v.name && v.name.toLowerCase().includes('other')) ||
                    (v.key && v.key.toLowerCase().includes('other'))
            );
        }
        return false;
    }, [businessPurposeValue]);

    const visibleFields = BusinessDetailsFields.filter((field) => {
        if (field.name === 'pleaseSpecifyOthersCompanyIsInterested')
            return showSpecifyOther;
        if (field?.name == 'otherBusinessSector') {
            const otherId = industryOptions?.find(
                (item: any) => item?.name?.trim() === 'Other'
            )?.key;
            if (industryValue?.includes(otherId)) {
                return true;
            }
            return false;
        }
        return true;
    });

    useEffect(() => {
        if (industryValue) {
            const otherId = industryOptions?.find(
                (item: any) => item?.name === 'Other'
            )?.key;
            if (!industryValue?.includes(otherId)) {
                setValue('otherBusinessSector', '');
            }
        }
    }, [industryValue]);
    return (
        <>
            {isLoading && <Loader />}
            <form onSubmit={handleSubmit(onSubmit)}>
                <ClayLayout.Row justify="start" className="basic_detail_form">
                    {visibleFields.map((field) => {
                        const colSize = [
                            'productAndServices',
                            'industry',
                            'doYouHaveAnyPreviousExperience'
                        ]?.includes(field?.name)
                            ? 12
                            : 6;
                        return field?.name == 'industry' ? (
                            <IndustriesSelect
                                control={control}
                                watch={watch}
                                errors={formErrors}
                                setValue={setValue}
                                register={register}
                                onIndustryOptionsChange={setIndustryOptions}
                                onSubIndustryOptionsChange={
                                    setSubIndustryOptions
                                }
                            />
                        ) : (
                            <ClayLayout.Col
                                key={field.name}
                                xl={colSize}
                                lg={colSize}
                                md={colSize}
                                sm={colSize}
                                xs={12}
                            >
                                <FormComponent
                                    {...field}
                                    register={register}
                                    control={control}
                                    errors={formErrors}
                                    watch={watch}
                                    setError={setError}
                                    errorTitle={field.errorMessage}
                                    options={dropdownOptions[field.name] || []}
                                />
                            </ClayLayout.Col>
                        );
                    })}
                </ClayLayout.Row>
                {previousCountryData?.length > 0 && (
                    <div className="ml-2">
                        <h1 className="table-heading">
                            Country Selected Previously
                        </h1>
                        <DynamicTable
                            columns={columns}
                            readOnly={true}
                            initialData={previousCountryData}
                        />
                    </div>
                )}

                <div
                    className={`ml-2 ${previousCountryData?.length ? 'mt-4' : 'mt-0'}`}
                >
                    <h1 className="table-heading">
                        Please select your countries that you wish to
                        expand/export to *
                    </h1>
                    <DynamicTable
                        columns={columns}
                        readOnly={false}
                        initialData={businessExpandData}
                        options={targetMarket}
                        onChange={setBusinessExpandData}
                        disableAdd={isAddDisabled}
                        errors={tableErrors}
                        marketPriority={marketPriority}
                        businessPurpose={businessPurpose}
                    />
                </div>
                <ClayLayout.Row
                    justify="start"
                    className="basic_detail_form mt-4"
                >
                    {previousExperienceField.map((field) => {
                        const colSize = [
                            'doYouHaveAnyPreviousExperience',
                            'additionalInformation',
                            'descriptionOfHowYourCompanyPlansToExpand'
                        ]?.includes(field?.name)
                            ? 12
                            : 6;
                        return (
                            <ClayLayout.Col
                                key={field.name}
                                xl={colSize}
                                lg={colSize}
                                md={colSize}
                                sm={colSize}
                                xs={12}
                            >
                                <FormComponent
                                    {...field}
                                    register={register}
                                    control={control}
                                    errors={formErrors}
                                    watch={watch}
                                    setError={setError}
                                    errorTitle={field.errorMessage}
                                    options={dropdownOptions[field.name] || []}
                                    desc={field?.desc}
                                />
                            </ClayLayout.Col>
                        );
                    })}
                </ClayLayout.Row>
                <div className="basic-details-button-section">
                    <Button
                        type="submit"
                        className="bottom-cut"
                        style={primaryButtonStyle}
                    >
                        <span className="bottom-cut-span"></span>
                        Submit
                    </Button>
                </div>
            </form>
        </>
    );
};

export default BusinessDetail;
